import { JwtHelperService } from '@auth0/angular-jwt';
import { distinctUntilChanged, startWith, delay, take } from 'rxjs/operators';
/**
 * Angular 2 decorators and services
 */
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, Observable } from 'rxjs';
import { LoadingPopupStore } from './lib/stores/loadingPopupStore/loadingPopupStore';
import * as fromRoot from './reducers';
import { Store } from '@ngrx/store';
import { DomEvent } from 'leaflet';
import * as employmentActions from './core/actions/employment';
import * as authActions from './core/actions/auth';
import { CurrentUserRequested } from './core/actions/auth';

/**
 * App Component
 * Top Level Component
 */
@Component({
  selector: 'mj-app',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./app.component.scss'],
  template: '<router-outlet></router-outlet>',
})
export class AppComponent implements OnInit, OnDestroy {
  public name = 'Mojob for Businesses';
  public loadingPopupStoreSubscription: Subscription;
  public loadingText: string = '';
  showProgressBar$: Observable<boolean>;

  activeItem: string;

  constructor(
    private _router: Router,
    private _translate: TranslateService,
    private _loadingPopupStore: LoadingPopupStore,
    private store: Store<fromRoot.State>
  ) {
    // Get status of progress bar
    this.showProgressBar$ = this.store
      .select(fromRoot.getProgressBarStatus)
      .pipe(
        startWith(null),
        delay(0)
      );
    // Adds translated languages to array
    _translate.addLangs(['en', 'no']);
    // this language will be used as a fallback when a translation isn't found in the current language
    _translate.setDefaultLang('en');

    if (!localStorage.getItem('language')) {
      this.getBrowserLang();
    } else {
      this.setLang(localStorage.getItem('language'));
    }

    const token = localStorage.getItem('id_token');

    // Using Rx's built in distinctUntilChanged feature to url changes for GA
    this._router.events
      .pipe(
        distinctUntilChanged((previous: any, current: any) => {
          // Subscribe to any NavigationEnd events where the url has changed
          if (current instanceof NavigationEnd) {
            return previous.url === current.url;
          }
          return true;
        })
      )
      .subscribe((x: any) => {
        (<any>window).dataLayer.push({ event: 'pageview' });
      });

    const jwtHelper = new JwtHelperService();
    if (!token || jwtHelper.isTokenExpired(token)) {
      // For now adding check for urls that should be
      // available anyways.
      // This check is only done on first load of web-mj-app.
      // Internal routes still functional and guarded with
      // canActivate
      const allowedOpenEndpoints = [
        '/reset-password/',
        '/invite',
        '/auth/',
        '/job/',
        '/complete/',
        '/register',
        '/login',
        '/company-register',
      ];
      let isAllowedEndpoint = false;
      const reachedEndpoint = window.location.pathname;

      for (const allowedOpenEndpoint of allowedOpenEndpoints) {
        if (reachedEndpoint.indexOf(allowedOpenEndpoint) > -1) {
          isAllowedEndpoint = true;
          break;
        }
      }

      if (!isAllowedEndpoint) {
        this._router.navigate(['login']);
      }
    } else {
      // wywolac loadEmployments
      this.store.dispatch(new employmentActions.LoadUserEmployments());
    }
  }

  ngOnInit() {
    this.activeItem = 'Post';

    this._router.events.subscribe(val => {
      window.scrollTo(0, 0);
    });

    this.loadingPopupStoreSubscription = this._loadingPopupStore
      .getState()
      .subscribe(loadingText => {
        this.loadingText = loadingText;
      });
  }

  ngOnDestroy() {
    this.loadingPopupStoreSubscription.unsubscribe();
  }

  checkItem(path) {
    return this.activeItem === path;
  }

  navigate(path) {
    this.activeItem = path;
    this._router.navigate([path]);
  }

  getBrowserLang() {
    const browserLanguage = this._translate.getBrowserLang();

    for (const lang of this._translate.getLangs()) {
      if (lang === browserLanguage && lang === 'en') {
        // the lang to use, if the lang isn't available, it will use the current loader to get them
        this._translate.use(browserLanguage);
        localStorage.setItem('language', browserLanguage);
        return;
      }

      // Norwegian nynorsk and bokmål defaults to lang code 'no'
      if (browserLanguage === 'nb' || browserLanguage === 'nn') {
        this._translate.use('no');
        localStorage.setItem('language', 'no');
        return;
      }
    }

    if (!localStorage.getItem('language')) {
      this._translate.use('en');
      localStorage.setItem('language', 'en');
    }
  }

  setLang(language) {
    for (const lang of this._translate.getLangs()) {
      if (lang === language) {
        this._translate.use(lang);
        return;
      }
    }
    // Fallback
    localStorage.setItem('language', 'en');
    this._translate.use('en');
  }
}

/**
 * Please review the https://github.com/AngularClass/angular2-examples/ repo for
 * more angular mj-app examples that you may copy/paste
 * (The examples may not be updated as quickly. Please open an issue on github for us to update it)
 * For help or questions please contact us at @AngularClass on twitter
 * or our chat on Slack at https://AngularClass.com/slack-join
 */
